import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Chip,
  Alert,
} from '@mui/material';
import { LineChart, BarChart, PieChart } from '@mui/x-charts';
import { analyticsApi } from '../../api/analytics';
import { QuoteConversionMetrics } from '../../types/analytics.types';

interface QuoteConversionMetricsProps {
  loading?: boolean;
}

export const QuoteConversionMetricsComponent: React.FC<QuoteConversionMetricsProps> = ({ 
  loading: externalLoading = false 
}) => {
  const [data, setData] = useState<QuoteConversionMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await analyticsApi.getQuoteConversionMetrics();
        setData(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch quote conversion metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading || externalLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  // Prepare chart data
  const monthlyTrendsData = data.monthlyTrends.map(trend => ({
    month: trend.month,
    conversionTime: trend.averageConversionTime,
    quotesCreated: trend.quotesCreated,
    quotesCompleted: trend.quotesCompleted,
    conversionRate: trend.conversionRate,
  }));

  const stageDistributionData = [
    { label: 'Draft', value: data.stageDistribution.draft, color: '#f44336' },
    { label: 'Issued', value: data.stageDistribution.issued, color: '#ff9800' },
    { label: 'Selected', value: data.stageDistribution.selected, color: '#2196f3' },
    { label: 'Completed', value: data.stageDistribution.completed, color: '#4caf50' },
    { label: 'Rejected', value: data.stageDistribution.rejected, color: '#9e9e9e' },
  ];

  const conversionRatesData = [
    { stage: 'Draft → Issued', rate: data.conversionRates.draftToIssued },
    { stage: 'Issued → Selected', rate: data.conversionRates.issuedToSelected },
    { stage: 'Selected → Completed', rate: data.conversionRates.selectedToCompleted },
  ];

  return (
    <Grid container spacing={3}>
      {/* Key Metrics Summary */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Quote Conversion Timescale Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="primary">
                    {data.totalAverageConversionTime}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Total Conversion (days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {data.conversionRates.overallConversion}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Conversion Rate
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {data.averageTimeToIssued}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time to Issue (days)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {data.averageTimeToSelected}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg. Time to Select (days)
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Commission Uplift Metrics */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Commission Uplift Analysis
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="success.main">
                    {data.averageCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average Commission Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="info.main">
                    {data.quotesWithUplift || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quotes with Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="warning.main">
                    {data.minCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Min Uplift
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Typography variant="h4" color="error.main">
                    {data.maxCommissionUplift?.toFixed(2) || '0.00'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Max Uplift
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Trends Chart */}
      <Grid item xs={12} lg={8}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Monthly Conversion Trends
            </Typography>
            <Box sx={{ height: 350 }}>
              <LineChart
                series={[
                  {
                    data: monthlyTrendsData.map(d => d.conversionTime),
                    label: 'Avg. Conversion Time (days)',
                    color: '#2196f3',
                  },
                  {
                    data: monthlyTrendsData.map(d => d.conversionRate),
                    label: 'Conversion Rate (%)',
                    color: '#4caf50',
                  },
                ]}
                xAxis={[
                  {
                    data: monthlyTrendsData.map(d => d.month),
                    scaleType: 'point',
                  },
                ]}
                height={350}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Conversion Rates Bar Chart */}
      <Grid item xs={12} lg={4}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Stage Conversion Rates
            </Typography>
            <Box sx={{ height: 350 }}>
              <BarChart
                series={[
                  {
                    data: conversionRatesData.map(d => d.rate),
                    label: 'Conversion Rate (%)',
                    color: '#ff9800',
                  },
                ]}
                xAxis={[
                  {
                    data: conversionRatesData.map(d => d.stage),
                    scaleType: 'band',
                  },
                ]}
                height={350}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Stage Distribution Pie Chart */}
      <Grid item xs={12} md={6}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Quote Stage Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <PieChart
                series={[
                  {
                    data: stageDistributionData,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  },
                ]}
                height={300}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Monthly Quote Volume */}
      <Grid item xs={12} md={6}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Monthly Quote Volume
            </Typography>
            <Box sx={{ height: 300 }}>
              <BarChart
                series={[
                  {
                    data: monthlyTrendsData.map(d => d.quotesCreated),
                    label: 'Quotes Created',
                    color: '#2196f3',
                  },
                  {
                    data: monthlyTrendsData.map(d => d.quotesCompleted),
                    label: 'Quotes Completed',
                    color: '#4caf50',
                  },
                ]}
                xAxis={[
                  {
                    data: monthlyTrendsData.map(d => d.month),
                    scaleType: 'band',
                  },
                ]}
                height={300}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Detailed Timing Breakdown */}
      <Grid item xs={12}>
        <Card sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          color: 'white'
        }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Average Time Breakdown by Stage
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToIssued} days`} 
                    color="info" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Draft → Issued
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToSelected} days`} 
                    color="warning" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Issued → Selected
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box textAlign="center" p={2}>
                  <Chip 
                    label={`${data.averageTimeToCompleted} days`} 
                    color="success" 
                    size="medium" 
                    sx={{ mb: 1, fontSize: '1.1rem', height: '36px' }}
                  />
                  <Typography variant="body2">
                    Selected → Completed
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
